// Design tokens + shared board-level consts/data. Pure data, no imports.

export const T = { bg: "#FDF7F2", ink: "#1C1917", ink2: "#57534E", line: "#EADDD0", white: "#FFFFFF" };
export const FONT = "Inter, system-ui, -apple-system, sans-serif";

// Perf: serve the website-compare + mobile frames as ~1800px-wide copies from our
// R2 (was full-page 3456px captures on tailormind-site that exceeded the GPU texture
// limit and made the board janky). Originals still live at
// tailormind-site.pages.dev/board/compare; re-run the scale+upload to refresh.
export const COMPARE = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/board/marketing";
export const FRAME_W = 900; // display width per site

export const SITES = [
  { id: "duna-productai", label: "duna.com/product/ai", tag: "REFERENCE", img: `${COMPARE}/duna-productai.jpg` },
  { id: "duna", label: "duna.com", tag: "REFERENCE", img: `${COMPARE}/duna.jpg` },
  { id: "ours", label: "ours — v1 (live homepage)", tag: "OURS", img: `${COMPARE}/ours.jpg` },
  { id: "ours-v2", label: "ours — v2 (live homepage)", tag: "OURS", img: `${COMPARE}/ours-v2.jpg` },
];
export const GAP = 360; // air between the site frames in the Website-design zone (also feeds the derived teardown offset)

export const HERO_BASE = "https://tailormind-site.pages.dev/board/hero";
// newest on top; keep the history, never delete
// EVERY hero render we've made, newest on top, full size — so Erez can inspect each
// (esp. whether clouds touch the TOP edge = bad). Flowers DEGRADE with each NB regen; pick
// the sharpest-flower base, then add sky PROGRAMMATICALLY (PIL canvas-extend), NOT Nano Banana.
export const HERO_CONCEPTS = [
  { id: "hero-tall2",  label: "tall2 · NB regen #2 (current LIVE base) — most sky, flowers SOFTEST",   img: `${HERO_BASE}/tall2.png` },
  { id: "hero-tall1",  label: "tall1 · NB regen #1 — flowers a bit soft",                              img: `${HERO_BASE}/tall1.png` },
  { id: "hero-orig",   label: "orig · ORIGINAL dawn, NO NB regen — SHARPEST flowers (add sky to THIS)", img: `${HERO_BASE}/orig-dawn.jpg` },
  { id: "hero-v21",    label: "v21 · clouds relit, blue center",                                       img: `${HERO_BASE}/v21-clouds.png` },
  { id: "hero-v20",    label: "v20 · center sky relit light-blue (clouds to sides)",                   img: `${HERO_BASE}/v20.jpg` },
  { id: "hero-v19",    label: "v19 · same relight, more saturated blue center",                        img: `${HERO_BASE}/v19.jpg` },
  { id: "hero-v18",    label: "v18 · duna-only, higher angle, pinker city",                            img: `${HERO_BASE}/v18.jpg` },
  { id: "hero-v17",    label: "v17 · steeper aerial + wildflowers",                                    img: `${HERO_BASE}/v17.jpg` },
  { id: "hero-v16",    label: "v16 · our city into duna, higher angle",                                img: `${HERO_BASE}/v16.jpg` },
  { id: "hero-v15",    label: "v15 · 4K high-detail, warm",                                            img: `${HERO_BASE}/v15.jpg` },
  { id: "hero-v14",    label: "v14",  img: `${HERO_BASE}/v14.png` },
  { id: "hero-v13",    label: "v13",  img: `${HERO_BASE}/v13.png` },
  { id: "hero-v12",    label: "v12",  img: `${HERO_BASE}/v12.png` },
  { id: "hero-v11",    label: "v11",  img: `${HERO_BASE}/v11.png` },
  { id: "hero-v10",    label: "v10",  img: `${HERO_BASE}/v10.png` },
  { id: "hero-v9",     label: "v9",   img: `${HERO_BASE}/v9.png` },
  { id: "hero-v8",     label: "v8",   img: `${HERO_BASE}/v8.png` },
  { id: "hero-v7",     label: "v7",   img: `${HERO_BASE}/v7.png` },
  { id: "hero-v6",     label: "v6",   img: `${HERO_BASE}/v6.png` },
  { id: "hero-v5",     label: "v5",   img: `${HERO_BASE}/v5.png` },
  { id: "hero-v4",     label: "v4",   img: `${HERO_BASE}/v4.png` },
  { id: "hero-v3",     label: "v3",   img: `${HERO_BASE}/v3.png` },
  { id: "hero-v2",     label: "v2",   img: `${HERO_BASE}/v2.png` },
  { id: "hero-v1",     label: "v1",   img: `${HERO_BASE}/v1.png` },
];

// Free HD stock candidates (Pexels, hotlinked at 2000px) — the "real photo" route:
// warm / pastel / dreamy landscapes in the duna look-and-feel. Local copies also in
// tailormind-site/public/board/stock/. Pick the vibe, then we mock the winner as hero.
export const PX = (id: string) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=2000`;
export const STOCK = [
  { id: "stock-01", label: "01 · layered mountains, peach + lavender sky", img: PX("1671630") },
  { id: "stock-02", label: "02 · pastel mountains, pink cloud streaks", img: PX("34640432") },
  { id: "stock-03", label: "03 · dreamy pink sunrise field", img: PX("13733285") },
  { id: "stock-04", label: "04 · warm pastel sea-of-clouds", img: PX("19063029") },
  { id: "stock-05", label: "05 · green alpine valley + mist (closest to duna)", img: PX("1269808") },
  { id: "stock-06", label: "06 · pastel mountains, warm gradient sky", img: PX("34742289") },
  { id: "stock-07", label: "07 · soft pastel mountains, sunrise", img: PX("21207375") },
  { id: "stock-08", label: "08 · misty pastel valley, layered", img: PX("11257990") },
  { id: "stock-09", label: "09 · green mountain lake, sunrise", img: PX("10050598") },
  { id: "stock-10", label: "10 · warm sea-of-clouds over hills", img: PX("11334337") },
];

export const WARM = "#D98E6A", PETROL = "#2B5560", SAGE = "#7FA37F", CREAMc = "#FDF7F2";

